import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Star, ChatCircleDots, Calendar, User, Funnel, TrendUp, TrendDown, Minus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ratingAPI } from '../lib/api';

const SAT_META = {
  muy_satisfecho: { label: 'Muy satisfactorio', color: 'bg-green-100 text-green-800 border-green-300', Icon: TrendUp },
  satisfecho: { label: 'Satisfactorio', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: TrendUp },
  poco_satisfecho: { label: 'Poco satisfactorio', color: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Minus },
  nada_satisfecho: { label: 'Nada satisfactorio', color: 'bg-red-100 text-red-800 border-red-300', Icon: TrendDown },
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StarBar = ({ stars, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2" data-testid={`star-bar-${stars}`}>
      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 w-10">{stars} ★</span>
      <div className="flex-1 h-2 bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
        <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-zinc-500 w-10 text-right">{count}</span>
    </div>
  );
};

const MyRatingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    if (!user?.user_id) return;
    setLoading(true);
    ratingAPI
      .getUserRatings(user.user_id)
      .then((r) => setRatings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRatings([]))
      .finally(() => setLoading(false));
  }, [user?.user_id]);

  const summary = useMemo(() => {
    const total = ratings.length;
    const sum = ratings.reduce((acc, r) => acc + (r.stars || 0), 0);
    const avg = total > 0 ? sum / total : 5.0;
    const byStar = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const bySat = { muy_satisfecho: 0, satisfecho: 0, poco_satisfecho: 0, nada_satisfecho: 0 };
    ratings.forEach((r) => {
      if (r.stars >= 1 && r.stars <= 5) byStar[r.stars]++;
      if (r.satisfaction && bySat[r.satisfaction] !== undefined) bySat[r.satisfaction]++;
    });
    const withComments = ratings.filter((r) => r.comment?.trim()).length;
    return { total, avg, byStar, bySat, withComments };
  }, [ratings]);

  const filtered = useMemo(() => {
    let out = [...ratings];
    if (filter !== 'all') {
      if (['1', '2', '3', '4', '5'].includes(filter)) out = out.filter((r) => r.stars === Number(filter));
      else if (filter === 'with_comments') out = out.filter((r) => r.comment?.trim());
      else out = out.filter((r) => r.satisfaction === filter);
    }
    if (sort === 'recent') out.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sort === 'oldest') out.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sort === 'highest') out.sort((a, b) => (b.stars || 0) - (a.stars || 0));
    else if (sort === 'lowest') out.sort((a, b) => (a.stars || 0) - (b.stars || 0));
    return out;
  }, [ratings, filter, sort]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="my-ratings-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
            <Star className="w-7 h-7 text-amber-400" weight="fill" />
            Mis Calificaciones
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Retroalimentación recibida de clientes y proveedores con los que has cerrado operaciones.
          </p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={() => navigate('/profile')} data-testid="back-to-profile">
          Volver al Perfil
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Avg rating */}
        <Card className="rounded-none border-amber-200 dark:border-amber-800">
          <CardContent className="p-6 text-center">
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Calificación promedio</p>
            <div className="text-5xl font-bold text-amber-500 flex items-center justify-center gap-1">
              {summary.avg.toFixed(1)}
              <Star className="w-8 h-8" weight="fill" />
            </div>
            <div className="flex justify-center gap-0.5 mt-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${n <= Math.round(summary.avg) ? 'text-amber-400' : 'text-zinc-300 dark:text-zinc-600'}`}
                  weight={n <= Math.round(summary.avg) ? 'fill' : 'regular'}
                />
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">{summary.total} {summary.total === 1 ? 'calificación' : 'calificaciones'}</p>
          </CardContent>
        </Card>

        {/* Star distribution */}
        <Card className="rounded-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por estrellas</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((s) => (
              <StarBar key={s} stars={s} count={summary.byStar[s] || 0} total={summary.total} />
            ))}
          </CardContent>
        </Card>

        {/* Satisfaction breakdown */}
        <Card className="rounded-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Satisfacción</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(SAT_META).map(([key, meta]) => {
              const count = summary.bySat[key] || 0;
              const pct = summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;
              return (
                <div key={key} className="flex items-center justify-between text-xs" data-testid={`sat-${key}`}>
                  <span className="text-zinc-700 dark:text-zinc-300">{meta.label}</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">{count} ({pct}%)</span>
                </div>
              );
            })}
            <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between text-xs">
              <span className="text-zinc-500">Con comentario</span>
              <span className="font-medium text-zinc-900 dark:text-zinc-100">{summary.withComments}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-none">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <Funnel className="w-4 h-4 text-zinc-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 rounded-none" data-testid="filter-select"><SelectValue placeholder="Filtrar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas ({summary.total})</SelectItem>
              <SelectItem value="with_comments">Con comentario ({summary.withComments})</SelectItem>
              <SelectItem value="5">5 estrellas ({summary.byStar[5]})</SelectItem>
              <SelectItem value="4">4 estrellas ({summary.byStar[4]})</SelectItem>
              <SelectItem value="3">3 estrellas ({summary.byStar[3]})</SelectItem>
              <SelectItem value="2">2 estrellas ({summary.byStar[2]})</SelectItem>
              <SelectItem value="1">1 estrella ({summary.byStar[1]})</SelectItem>
              <SelectItem value="muy_satisfecho">Muy satisfactorio</SelectItem>
              <SelectItem value="satisfecho">Satisfactorio</SelectItem>
              <SelectItem value="poco_satisfecho">Poco satisfactorio</SelectItem>
              <SelectItem value="nada_satisfecho">Nada satisfactorio</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-48 rounded-none" data-testid="sort-select"><SelectValue placeholder="Orden" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Más recientes</SelectItem>
              <SelectItem value="oldest">Más antiguas</SelectItem>
              <SelectItem value="highest">Mejor calificación</SelectItem>
              <SelectItem value="lowest">Peor calificación</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-xs text-zinc-500 ml-auto">Mostrando {filtered.length} de {summary.total}</span>
        </CardContent>
      </Card>

      {/* Ratings list */}
      {loading ? (
        <Card className="rounded-none"><CardContent className="p-12 text-center text-zinc-500">Cargando calificaciones...</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="rounded-none">
          <CardContent className="p-12 text-center">
            <Star className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-600 dark:text-zinc-400 font-medium">
              {summary.total === 0 ? 'Aún no tienes calificaciones' : 'No hay calificaciones con ese filtro'}
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              {summary.total === 0
                ? 'Las calificaciones aparecerán aquí cuando cierres tu primera transacción.'
                : 'Prueba ajustar los filtros o el orden.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const sat = SAT_META[r.satisfaction];
            return (
              <Card key={r.rating_id} className="rounded-none hover:border-orange-300 transition-colors" data-testid={`rating-item-${r.rating_id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-orange-600" weight="fill" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">
                          {r.from_user_name || 'Usuario'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                          <Badge variant="outline" className="rounded-none text-[10px]">
                            {r.from_user_role === 'cliente' ? 'Cliente' : 'Proveedor'}
                          </Badge>
                          {r.category && <span>· {r.category}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(r.created_at)}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-5 h-5 ${n <= (r.stars || 0) ? 'text-amber-400' : 'text-zinc-300 dark:text-zinc-600'}`}
                          weight={n <= (r.stars || 0) ? 'fill' : 'regular'}
                        />
                      ))}
                      <span className="ml-1 text-sm font-semibold">{r.stars || 0}</span>
                    </div>
                    {sat && (
                      <Badge className={`rounded-none text-xs ${sat.color}`}>
                        <sat.Icon className="w-3 h-3 mr-1" />{sat.label}
                      </Badge>
                    )}
                  </div>

                  {r.comment?.trim() ? (
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                      <ChatCircleDots className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-zinc-700 dark:text-zinc-300 italic whitespace-pre-wrap">
                        "{r.comment}"
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-400 mt-2 italic">Sin comentario escrito</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyRatingsPage;
